import axios from 'axios';
import { setupApiClient } from './apiConfig';
import {
    clearInstallerSession,
    persistInstallerSession
} from './installerSession';

setupApiClient();

/** 1 час бездействия → выход (staff) */
export const IDLE_TIMEOUT_MS = 60 * 60 * 1000;

/** Монтажник: не выкидывать по простою */
const INSTALLER_IDLE_TIMEOUT_MS =
    30 * 24 * 60 * 60 * 1000;

/** Обновлять JWT не чаще чем раз в N мс при активности */
const REFRESH_COOLDOWN_MS = 10 * 60 * 1000;

const getStoredRoleId = () => {
    try {
        return Number(
            JSON.parse(
                localStorage.getItem('user') ||
                    'null'
            )?.role_id
        );
    } catch {
        return null;
    }
};

export const isInstallerSession = () =>
    getStoredRoleId() === 5;

/** Как часто проверять простой */
const IDLE_CHECK_MS = 30 * 1000;

/** Троттлинг записи активности */
const ACTIVITY_THROTTLE_MS = 15 * 1000;

const ACTIVITY_KEY = 'lastActivityAt';
const LAST_REFRESH_KEY = 'lastTokenRefreshAt';

let activityTimer = null;
let idleIntervalId = null;
let refreshing = false;
let axiosConfigured = false;
let logoutInProgress = false;

export const touchActivity = () => {
    localStorage.setItem(
        ACTIVITY_KEY,
        String(Date.now())
    );
};

export const getLastActivityAt = () => {
    const raw =
        localStorage.getItem(
            ACTIVITY_KEY
        );

    const value = Number(raw);

    return Number.isFinite(value)
        ? value
        : 0;
};

export const clearSessionStorage = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('pageAccess');
    localStorage.removeItem(ACTIVITY_KEY);
    localStorage.removeItem(
        LAST_REFRESH_KEY
    );
    clearInstallerSession();
};

export const forceLogout = (
    reason = 'idle'
) => {
    if (logoutInProgress) {
        return;
    }

    logoutInProgress = true;

    clearSessionStorage();

    const path =
        window.location.pathname;

    if (path !== '/login') {
        const params =
            reason
                ? `?reason=${encodeURIComponent(reason)}`
                : '';

        window.location.href =
            `/login${params}`;
    } else {
        logoutInProgress = false;
    }
};

const getToken = () =>
    localStorage.getItem('token');

const isAuthenticated = () =>
    Boolean(
        getToken() &&
            localStorage.getItem('user')
    );

const refreshTokenIfNeeded =
    async (force = false) => {
        if (
            !isAuthenticated() ||
            refreshing
        ) {
            return;
        }

        const lastRefresh =
            Number(
                localStorage.getItem(
                    LAST_REFRESH_KEY
                ) || 0
            );

        if (
            !force &&
            Date.now() - lastRefresh <
            REFRESH_COOLDOWN_MS
        ) {
            return;
        }

        refreshing = true;

        try {
            const token = getToken();

            const response =
                await axios.post(
                    '/api/auth/refresh',
                    {},
                    {
                        headers: {
                            Authorization:
                                `Bearer ${token}`
                        }
                    }
                );

            if (
                response.data?.token
            ) {
                localStorage.setItem(
                    'token',
                    response.data.token
                );

                localStorage.setItem(
                    LAST_REFRESH_KEY,
                    String(Date.now())
                );

                if (
                    response.data.user
                ) {
                    localStorage.setItem(
                        'user',
                        JSON.stringify(
                            response
                                .data
                                .user
                        )
                    );
                }

                persistInstallerSession();
            }
        } catch (error) {
            const status =
                error.response
                    ?.status;

            if (
                status === 401
            ) {
                forceLogout(
                    'expired'
                );
            }
        } finally {
            refreshing = false;
        }
    };

const onUserActivity = () => {
    if (!isAuthenticated()) {
        return;
    }

    if (activityTimer) {
        return;
    }

    touchActivity();
    refreshTokenIfNeeded();

    if (isInstallerSession()) {
        persistInstallerSession();
    }

    activityTimer = setTimeout(
        () => {
            activityTimer = null;
        },
        ACTIVITY_THROTTLE_MS
    );
};

const checkIdleTimeout = () => {
    if (!isAuthenticated()) {
        return;
    }

    if (
        window.location.pathname ===
        '/login'
    ) {
        return;
    }

    const last =
        getLastActivityAt();

    if (!last) {
        touchActivity();
        return;
    }

    const limit = isInstallerSession()
        ? INSTALLER_IDLE_TIMEOUT_MS
        : IDLE_TIMEOUT_MS;

    if (Date.now() - last >= limit) {
        forceLogout('idle');
    }
};

/**
 * Слежение за активностью во всей системе.
 */
export const startSessionWatchers =
    () => {
        if (idleIntervalId) {
            return () => {};
        }

        if (isAuthenticated()) {
            if (!getLastActivityAt()) {
                touchActivity();
            }

            localStorage.setItem(
                LAST_REFRESH_KEY,
                String(Date.now())
            );
        }

        const events = [
            'mousemove',
            'mousedown',
            'keydown',
            'touchstart',
            'scroll',
            'click'
        ];

        events.forEach(eventName => {
            window.addEventListener(
                eventName,
                onUserActivity,
                {
                    passive: true,
                    capture: true
                }
            );
        });

        const onVisibility = () => {
            if (
                document.visibilityState ===
                'visible'
            ) {
                checkIdleTimeout();

                if (
                    isAuthenticated()
                ) {
                    touchActivity();
                    /*
                     * Монтажник: при возврате в APK
                     * сразу продлеваем JWT.
                     */
                    refreshTokenIfNeeded(
                        isInstallerSession()
                    );
                    onUserActivity();
                }
            }
        };

        document.addEventListener(
            'visibilitychange',
            onVisibility
        );

        idleIntervalId =
            setInterval(
                checkIdleTimeout,
                IDLE_CHECK_MS
            );

        checkIdleTimeout();

        return () => {
            events.forEach(
                eventName => {
                    window.removeEventListener(
                        eventName,
                        onUserActivity,
                        {
                            capture: true
                        }
                    );
                }
            );

            document.removeEventListener(
                'visibilitychange',
                onVisibility
            );

            if (idleIntervalId) {
                clearInterval(
                    idleIntervalId
                );
                idleIntervalId =
                    null;
            }

            if (activityTimer) {
                clearTimeout(
                    activityTimer
                );
                activityTimer =
                    null;
            }
        };
    };

/**
 * Глобальный axios: 401 → на логин.
 */
export const setupAxiosAuth = () => {
    if (axiosConfigured) {
        return;
    }

    axiosConfigured = true;

    axios.interceptors.response.use(
        response =>
            response,
        error => {
            const status =
                error.response
                    ?.status;

            const url =
                error.config
                    ?.url ||
                '';

            const isAuthCall =
                url.includes(
                    '/api/auth/login'
                ) ||
                url.includes(
                    '/api/auth/refresh'
                );

            if (
                status === 401 &&
                !isAuthCall &&
                isAuthenticated() &&
                window.location
                    .pathname !==
                    '/login'
            ) {
                forceLogout(
                    'expired'
                );
            }

            return Promise.reject(
                error
            );
        }
    );
};

/**
 * Вызывать после успешного логина.
 */
export const markSessionStarted =
    () => {
        touchActivity();
        localStorage.setItem(
            LAST_REFRESH_KEY,
            String(Date.now())
        );
        logoutInProgress = false;
        persistInstallerSession();
    };
